import {
  FiAlertCircle,
  FiDatabase,
  FiSearch,
  FiArrowDown,
  FiArrowUp,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { Box, Flex, Skeleton, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import Papa from 'papaparse';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Flags } from 'models/Global.interface';
import {
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TABLE_CELL_BORDER,
  PLAN_TABLE_HEADER_BG,
  PLAN_TABLE_STICKY_BG,
  PLAN_TEXT,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import {
  getNoheadersStatus,
  getSelectedAreaOfTable,
  getSelectedDataFileDetails,
  getSelectedVersionDetails,
  getSingleDataImportFiles,
  resetDataImport,
  setDataSourceApiPreviewEnabled,
  setDistinctCSV,
  setTableHeaderName,
} from 'redux/DataImportSlice';
import apiService from 'services';
import { readDataFile } from 'services/s3Service';
import { fontSize } from 'styles/revampTokens';
import { AppDispatch } from 'utils/GlobalHelpers';
import { DATA_IMPORT_PREVIEW_ROW_LIMIT } from 'utils/dataImportPreviewNotice';
import { getCsvExportBlockReason, getOriginalDownloadExtension } from 'utils/dataSourceDownload';
import { isDataSourceApiPreviewEnabled } from 'utils/dataSourceApiPreviewFlag';

import colors from './dataTheme';
import {
  applyPreviewTransforms,
  getPageWindow,
  isNumericColumn,
  type FiltersState,
  type PreviewColumn,
  type SortState,
} from './dataPreviewUtils';
import { CountPill, SectionTitle, StateBlock, cardStyle, controlStyle } from './detailUi';
import PreviewToolbar from './PreviewToolbar';
import useVersionDownload from './useVersionDownload';

const PAGE_SIZE = 50;

export interface Properties {
  flags?: Flags;
  /** Hides the search/filter/sort/columns/download controls (used while creating an action). */
  isReadOnly?: boolean;
}

const buildDistinctValues = (headers: string[], rows: any[], noHeaders: boolean) => {
  const letters: string[] = new Array(26)
    .fill(1)
    .map((_: number, index: number) => String.fromCodePoint(65 + index));
  const newHeaders = headers.map((item: string, headerIndex: number) => ({
    current: `${item}`,
    modified: noHeaders ? `Column ${letters[headerIndex]}` : `${item}`,
    distinctValues: [] as any[],
  }));
  rows.forEach((row: any) => {
    newHeaders.forEach((header: any) => {
      if (row[header.current]) {
        header.distinctValues = Array.from(
          new Set([...header.distinctValues, row[header.current]]),
        );
      }
    });
  });
  return newHeaders;
};

const DataPreview = ({ flags, isReadOnly = false }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const { modelId } = useParams();
  const selectedFileDetails = useSelector(getSelectedDataFileDetails);
  const selectedVersionDetails = useSelector(getSelectedVersionDetails);
  const tableSelectedAreas = useSelector(getSelectedAreaOfTable) || {};
  const noHeaders = useSelector(getNoheadersStatus);

  const dataSourceId = selectedFileDetails?.id;
  const versionId = selectedVersionDetails?.id;
  const useApiPreview = isDataSourceApiPreviewEnabled(flags);
  const { download, downloadingId } = useVersionDownload(dataSourceId, selectedFileDetails?.name);

  const [csvData, setCsvData] = useState<any[]>([]);
  const [headerNames, setHeaderNames] = useState<string[]>([]);
  const [customHeaders, setCustomHeaders] = useState<PreviewColumn[]>([]);
  const [fileUrl, setFileURL] = useState('');
  const [loader, setLoader] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [previewHasMore, setPreviewHasMore] = useState(false);
  const [totalRows, setTotalRows] = useState<number | null>(null);
  const [previewReady, setPreviewReady] = useState(false);

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FiltersState>({});
  const [sort, setSort] = useState<SortState | null>(null);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);

  // Leaving the detail screen clears the selected source, exactly as before.
  useEffect(
    () => () => {
      setFileURL('');
      dispatch(resetDataImport());
      setCsvData([]);
    },
    [],
  );

  useEffect(() => {
    dispatch(setDataSourceApiPreviewEnabled(useApiPreview));
  }, [dispatch, useApiPreview]);

  useEffect(() => {
    setSearch('');
    setFilters({});
    setSort(null);
    setHiddenColumns(new Set());
    setPage(1);
  }, [versionId]);

  useEffect(() => {
    if (headerNames.length === 0 || csvData.length === 0) return;
    const distinctCSV = buildDistinctValues(headerNames, csvData, noHeaders);
    setCustomHeaders(distinctCSV.map(({ current, modified }) => ({ current, modified })));
    dispatch(setDistinctCSV(distinctCSV));
  }, [noHeaders, headerNames, csvData, dispatch]);

  useEffect(() => {
    if (selectedFileDetails?.deatsilsPage === false) {
      setHeaderNames([]);
      setCsvData([]);
      setCustomHeaders([]);
    }
  }, [selectedFileDetails]);

  useEffect(() => {
    if (dataSourceId && modelId) {
      dispatch(getSingleDataImportFiles({ modelId, fileId: dataSourceId }));
    }
  }, [dataSourceId]);

  // Legacy path: read the file straight from S3 and parse it in the browser.
  useEffect(() => {
    if (useApiPreview || !selectedVersionDetails?.s3_file_url) return;
    readDataFile(selectedVersionDetails.s3_file_url.split('/').pop(), 'blox-data-import')
      .then((url: any) => setFileURL(url))
      .catch(() => setLoadError(true));
  }, [selectedVersionDetails?.s3_file_url, useApiPreview]);

  useEffect(() => {
    if (!fileUrl) return;
    setLoader(true);
    setLoadError(false);
    setCsvData([]);
    setHeaderNames([]);
    setCustomHeaders([]);
    dispatch(setDistinctCSV([]));
    fetch(fileUrl)
      .then((response) => response.text())
      .then((text) => {
        Papa.parse(text, {
          worker: true,
          header: true,
          stream: true,
          chunkSize: 1024,
          complete: (result: any) => {
            setHeaderNames(result.meta.fields || []);
            dispatch(setTableHeaderName(result?.meta?.fields));
            setCsvData(result.data || []);
            setLoader(false);
          },
        } as any);
      })
      .catch(() => {
        setLoader(false);
        setLoadError(true);
      });
  }, [fileUrl]);

  // Preferred path: the server returns a capped preview (500 rows) plus the real row count.
  useEffect(() => {
    if (!useApiPreview || !dataSourceId || !versionId) {
      setPreviewReady(false);
      return () => {};
    }

    let cancelled = false;
    setLoader(true);
    setLoadError(false);
    setPreviewReady(false);
    setCsvData([]);
    setHeaderNames([]);
    setCustomHeaders([]);
    setTotalRows(null);
    setPreviewHasMore(false);

    apiService
      .getVersionPreview(dataSourceId, versionId, {
        rows: DATA_IMPORT_PREVIEW_ROW_LIMIT,
        ...(noHeaders ? { missing_header: true } : {}),
      })
      .then((response) => {
        if (cancelled) return;
        const payload = response.data;
        const columns = payload?.columns || [];
        setHeaderNames(columns);
        dispatch(setTableHeaderName(columns));
        setCsvData(payload?.rows || []);
        setTotalRows(payload?.total_rows ?? null);
        setPreviewHasMore(Boolean(payload?.has_more));
        setPreviewReady(true);
        setLoader(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoader(false);
        setLoadError(true);
        setPreviewReady(false);
      });

    return () => {
      cancelled = true;
    };
  }, [useApiPreview, dataSourceId, versionId, noHeaders, dispatch]);

  const visibleColumns = useMemo(
    () => customHeaders.filter((header) => !hiddenColumns.has(header.current)),
    [customHeaders, hiddenColumns],
  );

  const processedRows = useMemo(
    () =>
      applyPreviewTransforms(csvData, {
        search,
        filters,
        sort,
        searchColumns: visibleColumns.map((column) => column.current),
      }),
    [csvData, search, filters, sort, visibleColumns],
  );

  const numericColumns = useMemo(
    () =>
      new Set(
        customHeaders
          .filter((header) => isNumericColumn(csvData, header.current))
          .map((header) => header.current),
      ),
    [customHeaders, csvData],
  );

  const pageCount = Math.max(1, Math.ceil(processedRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const firstRow = (currentPage - 1) * PAGE_SIZE;
  const pageRows = processedRows.slice(firstRow, firstRow + PAGE_SIZE);

  const hasData = customHeaders.length > 0 && csvData.length > 0;
  const isBlank = useApiPreview ? !previewReady && !loader : !fileUrl && !loader;
  const selectedColumns = Object.values(tableSelectedAreas) as string[];
  const hasActiveFilters =
    search.trim() !== '' || Object.values(filters).some((values) => values.length > 0);

  const summary = hasData
    ? previewHasMore && totalRows !== null
      ? `Showing ${csvData.length.toLocaleString()} of ${totalRows.toLocaleString()} rows`
      : `${csvData.length.toLocaleString()} rows`
    : '';

  const cycleSort = (column: string) => {
    setPage(1);
    if (sort?.column !== column) setSort({ column, direction: 'asc' });
    else if (sort.direction === 'asc') setSort({ column, direction: 'desc' });
    else setSort(null);
  };

  const clearFilters = () => {
    setSearch('');
    setFilters({});
    setPage(1);
  };

  const renderTable = () => (
    <Table
      variant='unstyled'
      size='sm'
      w='max-content'
      minW='100%'
      border='1px solid'
      borderColor={PLAN_TABLE_CELL_BORDER}
      borderRadius={PLAN_REVAMP_RADIUS_SM}
      sx={{
        borderCollapse: 'separate',
        borderSpacing: 0,
        // Grid lines between columns; the table's own border closes the box.
        'th, td': { borderRight: '1px solid', borderRightColor: PLAN_TABLE_CELL_BORDER },
        'th:last-of-type, td:last-of-type': { borderRight: 'none' },
        'tbody tr:last-of-type td': { borderBottom: 'none' },
        'thead tr:first-of-type th:first-of-type': { borderTopLeftRadius: PLAN_REVAMP_RADIUS_SM },
        'thead tr:first-of-type th:last-of-type': { borderTopRightRadius: PLAN_REVAMP_RADIUS_SM },
        'tbody tr:last-of-type td:first-of-type': {
          borderBottomLeftRadius: PLAN_REVAMP_RADIUS_SM,
        },
        'tbody tr:last-of-type td:last-of-type': {
          borderBottomRightRadius: PLAN_REVAMP_RADIUS_SM,
        },
      }}
    >
      <Thead position='sticky' top={0} zIndex={1}>
        <Tr>
          {visibleColumns.map((column, columnIndex) => {
            const isFirstColumn = columnIndex === 0;
            const isSorted = sort?.column === column.current;
            const isNumeric = numericColumns.has(column.current);
            return (
              <Th
                key={column.current}
                p={0}
                bg={PLAN_TABLE_HEADER_BG}
                position={isFirstColumn ? 'sticky' : undefined}
                left={isFirstColumn ? 0 : undefined}
                zIndex={isFirstColumn ? 3 : undefined}
                borderBottom='1px solid'
                borderColor={PLAN_TABLE_CELL_BORDER}
                textTransform='none'
                letterSpacing='normal'
                whiteSpace='nowrap'
              >
                <Flex
                  role='button'
                  alignItems='center'
                  justifyContent={isNumeric ? 'flex-end' : 'flex-start'}
                  gap='6px'
                  minW='120px'
                  maxW='320px'
                  px={3}
                  py='9px'
                  cursor={isReadOnly ? 'default' : 'pointer'}
                  onClick={isReadOnly ? undefined : () => cycleSort(column.current)}
                  _hover={{ '& .sort-hint': { opacity: 1 } }}
                >
                  <Text
                    {...PLAN_TEXT.tableComfortaa}
                    color={colors.textDark}
                    noOfLines={1}
                    title={column.modified}
                  >
                    {column.modified}
                  </Text>
                  {selectedColumns.includes(column.current) && (
                    <CheckIcon boxSize='10px' color={colors.primary} />
                  )}
                  <Box
                    className='sort-hint'
                    opacity={isSorted ? 1 : 0}
                    color={isSorted ? colors.primary : colors.textSecondary}
                    transition='opacity 0.12s ease'
                    flexShrink={0}
                    display='inline-flex'
                  >
                    {isSorted && sort?.direction === 'desc' ? (
                      <FiArrowDown size={12} />
                    ) : (
                      <FiArrowUp size={12} />
                    )}
                  </Box>
                </Flex>
              </Th>
            );
          })}
        </Tr>
      </Thead>
      <Tbody>
        {pageRows.map((row, rowIndex) => (
          <Tr key={firstRow + rowIndex} _hover={{ bg: colors.hover }}>
            {visibleColumns.map((column, columnIndex) => (
              <Td
                key={column.current}
                position={columnIndex === 0 ? 'sticky' : undefined}
                left={columnIndex === 0 ? 0 : undefined}
                zIndex={columnIndex === 0 ? 1 : undefined}
                px={3}
                py='9px'
                {...PLAN_TEXT.tableComfortaaCell}
                color={colors.textDark}
                textAlign={numericColumns.has(column.current) ? 'right' : 'left'}
                whiteSpace='nowrap'
                maxW='320px'
                overflow='hidden'
                textOverflow='ellipsis'
                borderBottom='1px solid'
                borderColor={PLAN_TABLE_CELL_BORDER}
                bg={
                  columnIndex === 0 || selectedColumns.includes(column.current)
                    ? PLAN_TABLE_STICKY_BG
                    : undefined
                }
                title={row[column.current]}
              >
                {row[column.current]}
              </Td>
            ))}
          </Tr>
        ))}
      </Tbody>
    </Table>
  );

  const renderBody = () => {
    if (loader) {
      // Table-shaped skeleton so the layout does not jump when the data arrives.
      return (
        <Box
          border='1px solid'
          borderColor={PLAN_TABLE_CELL_BORDER}
          borderRadius={PLAN_REVAMP_RADIUS_SM}
        >
          {Array.from({ length: 8 }, (_, rowIndex) => (
            <Flex
              key={rowIndex}
              h='38px'
              alignItems='center'
              gap={4}
              px={3}
              bg={rowIndex === 0 ? PLAN_TABLE_HEADER_BG : undefined}
              borderTop={rowIndex === 0 ? 'none' : '1px solid'}
              borderColor={PLAN_TABLE_CELL_BORDER}
            >
              {[2, 1, 1, 1, 1].map((weight, columnIndex) => (
                <Skeleton key={columnIndex} flex={weight} h='12px' borderRadius='4px' />
              ))}
            </Flex>
          ))}
        </Box>
      );
    }
    if (loadError) {
      return (
        <StateBlock
          tone='danger'
          icon={<FiAlertCircle size={22} />}
          title='Could not load this data'
          description='Check your connection and try again.'
        />
      );
    }
    if (isBlank || !hasData) {
      return (
        <StateBlock
          icon={<FiDatabase size={22} />}
          title='No data available'
          description='This version has no rows to preview.'
        />
      );
    }
    if (processedRows.length === 0) {
      return (
        <StateBlock
          icon={<FiSearch size={22} />}
          title='No matching rows'
          description='Try a different search or clear your filters.'
          action={
            hasActiveFilters ? (
              <Flex as='button' type='button' {...controlStyle} onClick={clearFilters}>
                Clear filters
              </Flex>
            ) : undefined
          }
        />
      );
    }
    return renderTable();
  };

  return (
    <Flex direction='column' {...cardStyle} flex={1} minH={0} overflow='hidden'>
      <Box px={4} pt={3} pb={3}>
        <Flex alignItems='baseline' gap={3} mb={isReadOnly ? 0 : 3} flexWrap='wrap'>
          <SectionTitle>Data preview</SectionTitle>
          {summary && <CountPill>{summary}</CountPill>}
        </Flex>
        {!isReadOnly && (
          <PreviewToolbar
            columns={customHeaders}
            rows={csvData}
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            filters={filters}
            onFiltersChange={(next) => {
              setFilters(next);
              setPage(1);
            }}
            sort={sort}
            onSortChange={(next) => {
              setSort(next);
              setPage(1);
            }}
            hiddenColumns={hiddenColumns}
            onHiddenColumnsChange={setHiddenColumns}
            onDownload={(format) => download(selectedVersionDetails, format)}
            originalLabel={`Download original (.${getOriginalDownloadExtension(
              selectedVersionDetails?.storage_format,
            )})`}
            csvBlockReason={
              selectedVersionDetails ? getCsvExportBlockReason(selectedVersionDetails) : null
            }
            isDownloading={downloadingId === versionId}
            canDownload={Boolean(versionId)}
          />
        )}
      </Box>

      {/* The inset lives on this wrapper, not on the scroller: padding on a scroll
          container leaves a strip above the sticky header where rows show through. */}
      <Flex
        direction='column'
        flex={1}
        minH='200px'
        borderTop='1px solid'
        borderColor={PLAN_TABLE_CELL_BORDER}
        px={4}
        pt={3}
        pb={3}
      >
        <Box flex={1} minH={0} overflow='auto' className='scroll'>
          {renderBody()}
        </Box>
      </Flex>

      <Flex
        alignItems='center'
        justifyContent='space-between'
        gap={3}
        px={4}
        py={2}
        flexShrink={0}
        flexWrap='wrap'
        borderTop='1px solid'
        borderColor={PLAN_TABLE_CELL_BORDER}
      >
        <Flex alignItems='center' gap={3} minW={0}>
          <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary}>
            {processedRows.length === 0
              ? 'No rows'
              : `Showing ${(firstRow + 1).toLocaleString()}–${(
                  firstRow + pageRows.length
                ).toLocaleString()} of ${processedRows.length.toLocaleString()} rows`}
          </Text>
          {previewHasMore && !isReadOnly && (
            <Flex
              as='button'
              type='button'
              {...controlStyle}
              h='28px'
              onClick={() => download(selectedVersionDetails, 'original')}
            >
              Download full dataset
            </Flex>
          )}
        </Flex>

        {pageCount > 1 && (
          <Flex alignItems='center' gap={1}>
            <Flex
              as='button'
              type='button'
              aria-label='Previous page'
              w='28px'
              h='28px'
              alignItems='center'
              justifyContent='center'
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              color={colors.textSecondary}
              opacity={currentPage === 1 ? 0.4 : 1}
              pointerEvents={currentPage === 1 ? 'none' : 'auto'}
              _hover={{ bg: colors.hover }}
              onClick={() => setPage(currentPage - 1)}
            >
              <FiChevronLeft size={16} />
            </Flex>
            {getPageWindow(currentPage, pageCount).map((entry) =>
              typeof entry === 'number' ? (
                <Flex
                  key={entry}
                  as='button'
                  type='button'
                  minW='28px'
                  h='28px'
                  px='6px'
                  alignItems='center'
                  justifyContent='center'
                  borderRadius={PLAN_REVAMP_RADIUS_SM}
                  bg={entry === currentPage ? colors.activeBg : 'transparent'}
                  color={entry === currentPage ? colors.primary : colors.textSecondary}
                  fontSize={fontSize.label}
                  fontWeight={500}
                  _hover={{ bg: entry === currentPage ? colors.activeBg : colors.hover }}
                  onClick={() => setPage(entry)}
                >
                  {entry}
                </Flex>
              ) : (
                <Text key={entry} px='4px' color={colors.textSecondary}>
                  …
                </Text>
              ),
            )}
            <Flex
              as='button'
              type='button'
              aria-label='Next page'
              w='28px'
              h='28px'
              alignItems='center'
              justifyContent='center'
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              color={colors.textSecondary}
              opacity={currentPage === pageCount ? 0.4 : 1}
              pointerEvents={currentPage === pageCount ? 'none' : 'auto'}
              _hover={{ bg: colors.hover }}
              onClick={() => setPage(currentPage + 1)}
            >
              <FiChevronRight size={16} />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default withLDConsumer()(DataPreview);
